import { DateTime } from 'luxon';
import i18n from 'i18next';

const isCurrentYear = date => date.year === DateTime.local().year;

export const formatDateString = date =>
  date
    ? date.toLocaleString({
        month: 'short',
        day: 'numeric',
        year: isCurrentYear(date) ? undefined : 'numeric',
      })
    : '';

export const getFormattedDate = (dates: [DateTime, DateTime]) =>
  dates?.[0] || dates?.[1]
    ? `${formatDateString(dates[0])} - ${formatDateString(dates[1])}`
    : '';

export type DateRangeName =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'this-month'
  | 'this-quarter'
  | 'this-year'
  | 'last-week'
  | 'last-month'
  | 'last-quarter'
  | 'last-year'
  | 'custom'
  | 'lpLast7Days'
  | 'lpLast30Days'
  | 'lpLast60Days'
  | 'lpLast90Days'
  | 'lpLast365Days';

export type DateRanges = {
  [key in DateRangeName]: [DateTime, DateTime];
};

export const makeDateRanges = (anchorDate?: DateTime): DateRanges => {
  const anchor = anchorDate ?? DateTime.local();
  const lpAnchor = anchorDate ?? DateTime.local().minus({ days: 2 });
  return {
    today: [anchor.startOf('day'), anchor.endOf('day')],
    yesterday: [
      anchor.plus({ days: -1 }).startOf('day'),
      anchor.plus({ days: -1 }).endOf('day'),
    ],
    'this-week': [anchor.startOf('week'), anchor.endOf('day')],
    'last-week': [
      anchor.plus({ days: -7 }).startOf('week'),
      anchor.plus({ days: -7 }).endOf('week'),
    ],
    'this-month': [anchor.startOf('month'), anchor.endOf('day')],
    'last-month': [
      anchor.plus({ months: -1 }).startOf('month'),
      anchor.plus({ months: -1 }).endOf('month'),
    ],
    'this-quarter': [anchor.startOf('quarter'), anchor.endOf('day')],
    'last-quarter': [
      anchor.plus({ quarters: -1 }).startOf('quarter'),
      anchor.plus({ quarters: -1 }).endOf('quarter'),
    ],
    'this-year': [anchor.startOf('year'), anchor.endOf('day')],
    'last-year': [
      anchor.plus({ years: -1 }).startOf('year'),
      anchor.plus({ years: -1 }).endOf('year'),
    ],
    lpLast7Days: [
      lpAnchor.minus({ days: 7 }).startOf('day'),
      lpAnchor.endOf('day'),
    ],
    lpLast30Days: [
      lpAnchor.minus({ days: 30 }).startOf('day'),
      lpAnchor.endOf('day'),
    ],
    lpLast60Days: [
      lpAnchor.minus({ days: 60 }).startOf('day'),
      lpAnchor.endOf('day'),
    ],
    lpLast90Days: [
      lpAnchor.minus({ days: 90 }).startOf('day'),
      lpAnchor.endOf('day'),
    ],
    lpLast365Days: [
      lpAnchor.minus({ days: 365 }).startOf('day'),
      lpAnchor.endOf('day'),
    ],
    custom: [anchor, anchor],
  };
};

export type DateRangeOption = {
  id: DateRangeName;
  label: string;
};

const dateRangeOptions: () => {
  [key in DateRangeName]: DateRangeOption;
} = () => ({
  today: {
    id: 'today',
    label: i18n.t('forms.fields.dateRange.options.today'),
  },
  yesterday: {
    id: 'yesterday',
    label: i18n.t('forms.fields.dateRange.options.yesterday'),
  },
  'this-week': {
    id: 'this-week',
    label: i18n.t('forms.fields.dateRange.options.thisWeek'),
  },
  'this-month': {
    id: 'this-month',
    label: i18n.t('forms.fields.dateRange.options.thisMonth'),
  },
  'this-quarter': {
    id: 'this-quarter',
    label: i18n.t('forms.fields.dateRange.options.thisQuarter'),
  },
  'this-year': {
    id: 'this-year',
    label: i18n.t('forms.fields.dateRange.options.thisYear'),
  },
  'last-week': {
    id: 'last-week',
    label: i18n.t('forms.fields.dateRange.options.lastWeek'),
  },
  'last-month': {
    id: 'last-month',
    label: i18n.t('forms.fields.dateRange.options.lastMonth'),
  },
  'last-quarter': {
    id: 'last-quarter',
    label: i18n.t('forms.fields.dateRange.options.lastQuarter'),
  },
  'last-year': {
    id: 'last-year',
    label: i18n.t('forms.fields.dateRange.options.lastYear'),
  },
  lpLast7Days: {
    id: 'lpLast7Days',
    label: i18n.t('forms.fields.dateRange.options.lpLast7Days'),
  },
  lpLast30Days: {
    id: 'lpLast30Days',
    label: i18n.t('forms.fields.dateRange.options.lpLast30Days'),
  },
  lpLast60Days: {
    id: 'lpLast60Days',
    label: i18n.t('forms.fields.dateRange.options.lpLast60Days'),
  },
  lpLast90Days: {
    id: 'lpLast90Days',
    label: i18n.t('forms.fields.dateRange.options.lpLast90Days'),
  },
  lpLast365Days: {
    id: 'lpLast365Days',
    label: i18n.t('forms.fields.dateRange.options.lpLast365Days'),
  },
  custom: {
    id: 'custom',
    label: i18n.t('forms.fields.dateRange.options.customDateRange'),
  },
});

/**
 * Returns an array of DateRangeOption objects for the specified range names. Now the preferred
 * method over the deprecated predefinedRangeOptions function. It is better to explicitly include
 * rather than exclude.
 * @param include - an array of DateRangeName to include in the results
 * @returns an array of DateRangeOption objects corresponding to the included range names
 */
export const getDateRangeOptions = (
  include: DateRangeName[],
): DateRangeOption[] => {
  const options = dateRangeOptions();
  return include.map(name => options[name]).filter(Boolean);
};

/**
 *  Keeping this method for backwards compatibility
 * @deprecated Use getDateRangeOptions with include parameters instead.
 * @param exclude - an array of DateRangeName to exclude from the results
 * @returns an array of DateRangeOption objects excluding the specified ranges
 */
export const predefinedRangeOptions = (
  exclude: DateRangeName[] = [],
): DateRangeOption[] => {
  const finalExclude: DateRangeName[] = [
    'lpLast7Days',
    'lpLast30Days',
    'lpLast60Days',
    'lpLast90Days',
    'lpLast365Days',
    ...exclude,
  ];

  const options = dateRangeOptions();
  const filteredOptions = Object.keys(options)
    .filter(key => !finalExclude.includes(key as DateRangeName))
    .map(key => options[key]);

  return filteredOptions;
};

export const getDateRangeName = (pickerDates, dateRanges) => {
  const entry = Object.entries(dateRanges).find(
    ([_name, dates]) =>
      dates[0]?.toISODate() === pickerDates[0]?.toISODate() &&
      dates[1]?.toISODate() === pickerDates[1]?.toISODate(),
  );

  return entry?.[0] ?? 'custom';
};
